import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DynamicContent } from "@workspace/db";
import { useLanguage } from "./useLanguage";

interface ContentMap {
  [key: string]: string;
}

interface UseDynamicContentReturn {
  content: ContentMap;
  getContent: (key: string, defaultValue?: string) => string;
  isLoading: boolean;
  error: Error | null;
  getContentByCategory: (category: string) => ContentMap;
}

export function useDynamicContent(): UseDynamicContentReturn {
  const { language } = useLanguage();
  const { data: dynamicContent, isLoading, error } = useQuery<DynamicContent[]>({
    queryKey: ["/api/content", { lang: language }],
    queryFn: async () => {
      const res = await fetch(`/api/content?lang=${encodeURIComponent(language)}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch content");
      return res.json();
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Create a map of content for easy access
  const content: ContentMap = {};
  if (dynamicContent) {
    dynamicContent.forEach((item) => {
      content[item.key] = item.value;
    });
  }

  // Helper function to get content by key with fallback
  const getContent = useCallback(
    (key: string, defaultValue: string = ""): string => {
      return content[key] || defaultValue;
    },
    [dynamicContent]
  );

  // Helper function to get content by category
  const getContentByCategory = useCallback(
    (category: string): ContentMap => {
      const categoryContent: ContentMap = {};
      if (dynamicContent) {
        dynamicContent
          .filter((item) => item.category === category)
          .forEach((item) => {
            categoryContent[item.key] = item.value;
          });
      }
      return categoryContent;
    },
    [dynamicContent]
  );

  return {
    content,
    getContent,
    isLoading,
    error,
    getContentByCategory,
  };
}

// Hook for getting content by specific category
export function useDynamicContentByCategory(category: string) {
  const { language } = useLanguage();
  const { data: dynamicContent, isLoading, error } = useQuery<DynamicContent[]>({
    queryKey: ["/api/content", "category", category, { lang: language }],
    queryFn: async () => {
      const res = await fetch(
        `/api/content/category/${encodeURIComponent(category)}?lang=${encodeURIComponent(language)}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to fetch category content");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const content: ContentMap = {};
  if (dynamicContent) {
    dynamicContent.forEach((item) => {
      content[item.key] = item.value;
    });
  }

  const getContent = useCallback(
    (key: string, defaultValue: string = ""): string => {
      return content[key] || defaultValue;
    },
    [dynamicContent]
  );

  return {
    content,
    getContent,
    isLoading,
    error,
  };
}

// Higher-order component to provide dynamic content to components
export function withDynamicContent<T extends object>(
  Component: React.ComponentType<T & { content: ContentMap; getContent: (key: string, defaultValue?: string) => string }>
) {
  return function WrappedComponent(props: T) {
    const { content, getContent } = useDynamicContent();
    
    return <Component {...props} content={content} getContent={getContent} />;
  };
}

// Custom hook for specific content keys (with fallbacks)
export function useContentKeys(keys: Record<string, string>) {
  const { getContent } = useDynamicContent();
  
  const contentValues: Record<string, string> = {};
  Object.entries(keys).forEach(([aliasKey, contentKey]) => {
    contentValues[aliasKey] = getContent(contentKey, contentKey); // Use key as fallback
  });
  
  return contentValues;
}

// Pre-configured hooks for common categories
export function useNavigationContent() {
  return useDynamicContentByCategory("navigation");
}

export function useButtonContent() {
  return useDynamicContentByCategory("buttons");
}

export function useFormContent() {
  return useDynamicContentByCategory("forms");
}

export function useMessageContent() {
  return useDynamicContentByCategory("messages");
}

export function useRatingContent() {
  return useDynamicContentByCategory("ratings");
}

export function useBrandingContent() {
  return useDynamicContentByCategory("branding");
}

export function usePageTitleContent() {
  return useDynamicContentByCategory("page_titles");
}

export function useCompatibilityContent() {
  return useDynamicContentByCategory("compatibility");
}

export function useDashboardContent() {
  return useDynamicContentByCategory("dashboard");
}

export function useAnalysisContent() {
  return useDynamicContentByCategory("analysis");
}